import type { BrandKit, KitPicture, KitSwatch, KitTypeStyle } from '../../services/work';

/**
 * What the Brand tab shows of a brand, worked out apart from the tab so it can be tested.
 *
 * The tab opens on the ESSENTIALS — the things placed most: the logo, the mark, the
 * brand's own colours, a heading and body text — at full size, and everything else is one
 * row away (Tarek, 2026-09-30: option E of six, with the brand's band on top).
 */

export type BrandKind = 'logos' | 'colours' | 'type' | 'icons' | 'graphics' | 'photos';

const groupOf = (logo: KitPicture): string => logo.group ?? (/^(Mark|Wordmark|Monogram|Lockup)\b/.exec(logo.name)?.[1] ?? 'Logo');

export interface Essentials {
  /** The brand's main logo: what its band shows, and places. */
  readonly logo: KitPicture | null;
  /** Its other forms, one of each — the mark, the wordmark, the monogram — for the tiles under the band. */
  readonly others: readonly KitPicture[];
  /** The brand's own colours — primary and secondary, not the status ones or the ramps. */
  readonly colours: readonly KitSwatch[];
  /** The largest of the brand's type styles, and the one it reads in. */
  readonly heading: KitTypeStyle | null;
  readonly body: KitTypeStyle | null;
}

const MOST_COLOURS = 6;
const MOST_OTHERS = 3;

export function essentials(kit: BrandKit): Essentials {
  const { logos } = kit.graphics;
  const logo = logos.find((one) => groupOf(one) === 'Logo') ?? logos[0] ?? null;
  // One of each other form, in the order they are reached for. Never the band's logo again:
  // a brand with one logo shows it once.
  const others: KitPicture[] = [];
  for (const group of ['Mark', 'Wordmark', 'Monogram', 'Lockup']) {
    const found = logos.find((one) => one !== logo && groupOf(one) === group);
    if (found && others.length < MOST_OTHERS) others.push(found);
  }
  const named = kit.colours.filter((group) => !group.ramp);
  // Status colours say "error" and "success"; they are not what a poster is painted in.
  const own = named.filter((group) => !/^status$/i.test(group.name));
  const colours = (own.length > 0 ? own : named).flatMap((group) => group.swatches).slice(0, MOST_COLOURS);
  const heading = kit.styles.length > 0 ? kit.styles.reduce((a, b) => (b.size > a.size ? b : a)) : null;
  const body = kit.styles.find((style) => /^(body|paragraph|text|p)\b/i.test(style.role)) ?? kit.styles.filter((style) => style !== heading && style.size >= 13).sort((a, b) => a.size - b.size)[0] ?? null;
  return { logo, others, colours, heading, body: body === heading ? null : body };
}

/** What the rail wears for the brand: its mark, else its logo. */
export function railMark(kit: BrandKit): KitPicture | null {
  const { logo, others } = essentials(kit);
  return others.find((one) => groupOf(one) === 'Mark') ?? others.find((one) => groupOf(one) === 'Monogram') ?? logo;
}

const GROUPS: readonly (readonly [string, readonly string[]])[] = [
  ['Logo', ['Logo']],
  ['Mark and Wordmark', ['Mark', 'Wordmark']],
  ['Monogram', ['Monogram']],
  ['Lockups', ['Lockup']],
];

/** The brand's logos by what each is, in the order a brand book shows them. Empty groups are left out. */
export function logoGroups(logos: readonly KitPicture[]): { title: string; logos: KitPicture[] }[] {
  const out = GROUPS.map(([title, kinds]) => ({ title, logos: logos.filter((logo) => kinds.includes(groupOf(logo))) })).filter((group) => group.logos.length > 0);
  const known = new Set(out.flatMap((group) => group.logos));
  const rest = logos.filter((logo) => !known.has(logo));
  return rest.length > 0 ? [...out, { title: 'More', logos: rest }] : out;
}

/**
 * The rows under the essentials: each kind the brand has MORE of than is already on
 * screen, with how many it has in all. A row that would open onto what is already shown —
 * "All Logos 1" under the one logo — is not drawn.
 */
export function brandRows(kit: BrandKit): { kind: BrandKind; label: string; count: number }[] {
  const { logos, icons, patterns, supergraphics, characters, imagery } = kit.graphics;
  const shown = essentials(kit);
  const colours = kit.colours.filter((group) => !group.ramp).reduce((sum, group) => sum + group.swatches.length, 0) + kit.colours.filter((group) => group.ramp).length + kit.gradients.length;
  const rows: { kind: BrandKind; label: string; count: number; more: boolean }[] = [
    { kind: 'logos', label: 'All Logos', count: logos.length, more: logos.length > (shown.logo ? 1 : 0) + shown.others.length },
    { kind: 'colours', label: 'All Colours', count: colours, more: colours > shown.colours.length },
    { kind: 'type', label: 'Type Styles', count: kit.styles.length, more: kit.styles.length > (shown.heading ? 1 : 0) + (shown.body ? 1 : 0) },
    { kind: 'icons', label: 'All Icons', count: icons.length, more: icons.length > 0 },
    { kind: 'graphics', label: 'All Graphics', count: supergraphics.length + patterns.length + characters.length, more: supergraphics.length + patterns.length + characters.length > 0 },
    { kind: 'photos', label: 'All Photos', count: imagery.length, more: imagery.length > 0 },
  ];
  return rows.filter((row) => row.more).map(({ kind, label, count }) => ({ kind, label, count }));
}

/** Whether the brand has anything at all to put on a page. */
export function isBare(kit: BrandKit): boolean {
  const { logos, icons, patterns, supergraphics, characters, imagery } = kit.graphics;
  return logos.length + icons.length + patterns.length + supergraphics.length + characters.length + imagery.length + kit.colours.length + kit.gradients.length + kit.styles.length === 0;
}

/** Whether a colour is light enough that what sits on it should be dark. */
export function isLight(hex: string): boolean {
  const hit = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!hit) return false;
  const [r, g, b] = [hit[1]!, hit[2]!, hit[3]!].map((part) => Number.parseInt(part, 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.6;
}

/** The colour the brand's band is painted: the ground of its logo, else its first colour. Null when it has neither. */
export function bandColour(kit: BrandKit): string | null {
  return essentials(kit).logo?.ground ?? kit.colours.find((group) => !group.ramp)?.swatches[0]?.hex ?? null;
}
